import * as THREE from 'three';
import { litMaterial } from '../core/materials';
import { creatureModel } from '../entities/models';
import type { BossAttack, EnemyDef } from '../game/content';
import { enemyById } from '../game/content';
import { enemyText, t as tr } from '../i18n';
import type { UiKey } from '../i18n/en.ts';
import { ELITE_AFFIX_COLORS, eliteDamageMultiplier, rollEliteAffix, warlordSpeedMultiplier, type EliteAffix } from '../game/eliteAffix';
import { clearElementalStatus, emptyElementalStatus, type ElementalStatus } from '../game/elements';
import { ELITE_HP_MULT, ELITE_TIMES, dmgScale, hpScale, maxAlive, rosterWeights, spawnRate, stormMultipliers } from '../game/rules';
import type { World } from '../game/World';
import { rgb } from '../utils/color';
import { SpatialGrid } from './SpatialGrid';
import { uploadFirst } from '../utils/gpu';

const CAPACITY = 420;
const NO_WEIGHTS: Array<[string, number]> = [];
const CULL_MARGIN = 3; // world units beyond the view an enemy is still drawn: covers its size, its shadow and the camera's one-frame lag
const DARK_MAP_GLOW = 0.25; // enemy self-glow on dark-ground maps (Ashen, Crypt, Void), as a fraction of its tint
const BULLETS = 400;
const RING_EVENTS = [75, 255, 400];
const FLOCK_EVENTS = [150, 330];
const BULLET_COLOR = new THREE.Color('#ff3a6a');

export class Enemy {
  alive = false;
  // Still referenced by Enemies.active (cleared on compaction). A slot killed and respawned
  // in the same frame must not be listed twice, or the spatial grid's linked list cycles.
  listed = false;
  serial = 0;
  def!: EnemyDef;
  group!: RenderGroup;
  x = 0;
  z = 0;
  kx = 0;
  kz = 0;
  hp = 0;
  maxHp = 0;
  damage = 0;
  speed = 0;
  radius = 0.5;
  mass = 1;
  scale = 1;
  xp = 0;
  slowUntil = 0;
  slowAmt = 0;
  flash = 0;
  state = 0;
  timer = 0;
  cd = 0;
  dirX = 0;
  dirZ = 0;
  lockUntil = 0;
  facing = 0;
  phase = 0;
  born = 0;
  elite = false;
  eliteAffix: EliteAffix | null = null;
  vulnUntil = 0;
  readonly elemStatus: ElementalStatus = emptyElementalStatus();
  boss = false;
  enraged = false;
  seq = 0;
  readonly immune = new Float32Array(8);
  constructor(readonly index: number) {}
}

interface RenderGroup {
  body: THREE.InstancedMesh;
  glow: THREE.InstancedMesh | null;
  n: number;
}

interface Bullet {
  active: boolean;
  x: number;
  z: number;
  vx: number;
  vz: number;
  life: number;
  damage: number;
  radius: number;
}

export interface Solid {
  x: number;
  z: number;
  r: number;
}

export class Enemies {
  readonly root = new THREE.Group();
  readonly pool: Enemy[] = [];
  readonly active: Enemy[] = [];
  boss: Enemy | null = null;
  solids: Solid[] = [];
  spawnRadius = 22;
  hostile = 0;
  braziers = 0;
  private readonly free: number[] = [];
  private readonly groups = new Map<string, RenderGroup>();
  private readonly grid = new SpatialGrid(2, 56, CAPACITY);
  private readonly ids = new Int32Array(CAPACITY);
  private readonly scratch = new Int32Array(CAPACITY);
  private readonly bullets: Bullet[] = [];
  private readonly warlords: Enemy[] = [];
  private readonly bulletMesh: THREE.InstancedMesh;
  private serialCounter = 0;
  private spawnAcc = 0;
  private eventsDone = new Set<string>();
  private nextBrazier = 12;
  private readonly matrix = new THREE.Matrix4();
  private readonly fxColor = new THREE.Color();
  private readonly frustum = new THREE.Frustum();
  private readonly viewProj = new THREE.Matrix4();
  private readonly bounds = new THREE.Sphere();
  private readonly quat = new THREE.Quaternion();
  private readonly posV = new THREE.Vector3();
  private readonly scaleV = new THREE.Vector3();
  private readonly color = new THREE.Color();
  private readonly up = new THREE.Vector3(0, 1, 0);
  private readonly xs = (i: number) => this.pool[i].x;
  private readonly zs = (i: number) => this.pool[i].z;

  constructor(private readonly world: World) {
    for (let i = 0; i < CAPACITY; i += 1) {
      this.pool.push(new Enemy(i));
      this.free.push(CAPACITY - 1 - i);
    }
    for (let i = 0; i < BULLETS; i += 1) this.bullets.push({ active: false, x: 0, z: 0, vx: 0, vz: 0, life: 0, damage: 0, radius: 0.3 });
    this.bulletMesh = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(0.5, 1), new THREE.MeshBasicMaterial({ color: '#ffffff' }), BULLETS);
    this.bulletMesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(BULLETS * 3), 3);
    this.bulletMesh.frustumCulled = false;
    this.bulletMesh.count = 0;
    this.bulletMesh.visible = false;
    this.root.add(this.bulletMesh);
  }

  // --- lifecycle ---

  prepareStage(ids: string[]): void {
    this.clear();
    for (const g of this.groups.values()) {
      this.root.remove(g.body);
      g.body.geometry.dispose();
      (g.body.material as THREE.Material).dispose();
      if (g.glow) {
        this.root.remove(g.glow);
        g.glow.geometry.dispose();
        (g.glow.material as THREE.Material).dispose();
      }
    }
    this.groups.clear();
    for (const id of new Set(ids)) this.ensureGroup(enemyById(id));
  }

  clear(): void {
    for (const e of this.active) {
      e.alive = false;
      e.listed = false;
    }
    this.active.length = 0;
    this.free.length = 0;
    for (let i = CAPACITY - 1; i >= 0; i -= 1) this.free.push(i);
    for (const b of this.bullets) b.active = false;
    this.bulletMesh.count = 0;
    this.bulletMesh.visible = false;
    this.boss = null;
    this.hostile = 0;
    this.braziers = 0;
    this.spawnAcc = 0;
    this.eventsDone.clear();
    this.nextBrazier = 12;
    this.render(0);
  }

  spawn(def: EnemyDef, x: number, z: number, elite = false): Enemy | null {
    const index = this.free.pop();
    if (index === undefined) return null;
    const w = this.world;
    const e = this.pool[index];
    const t = w.time;
    e.alive = true;
    e.def = def;
    e.group = this.ensureGroup(def);
    e.serial = ++this.serialCounter;
    e.x = x;
    e.z = z;
    e.kx = e.kz = 0;
    e.boss = def.behavior === 'boss';
    e.elite = elite;
    e.eliteAffix = elite ? rollEliteAffix(w.rng) : null;
    e.vulnUntil = 0;
    clearElementalStatus(e.elemStatus);
    const tier = w.stormTier ?? 0;
    const hpMul = e.boss ? stormMultipliers(tier).hp * (t > w.stage.duration ? Math.pow(1.25, (t - w.stage.duration) / 120) : 1) : def.behavior === 'static' ? 1 : hpScale(t, w.stage, tier) * (elite ? ELITE_HP_MULT : 1);
    e.maxHp = e.hp = def.hp * hpMul;
    e.damage = def.damage * dmgScale(t, w.stage, tier) * (elite ? 1.5 : 1);
    e.speed = def.speed * (1 + w.stageIndex * 0.025) * (elite ? 0.9 : 1) * (w.enemySpeedMult ?? 1);
    e.radius = def.radius * (elite ? 1.6 : 1);
    e.scale = def.scale * (elite ? 1.6 : 1);
    e.mass = def.mass * (elite ? 6 : 1);
    e.xp = def.xp * w.stage.xpMult * (elite ? 1 : 1);
    e.slowUntil = 0;
    e.slowAmt = 0;
    e.flash = 0;
    e.state = 0;
    e.timer = 0;
    e.cd = 1 + w.rng() * 2;
    e.lockUntil = 0;
    e.facing = Math.atan2(w.player.x - x, w.player.z - z);
    e.phase = w.rng() * 10;
    e.born = t;
    e.enraged = false;
    e.seq = Math.floor(w.rng() * 3);
    e.immune.fill(0);
    if (!e.listed) {
      e.listed = true;
      this.active.push(e);
    }
    if (def.behavior === 'static') this.braziers += 1;
    else this.hostile += 1;
    if (e.boss) {
      this.boss = e;
      e.cd = 2.5;
    }
    return e;
  }

  kill(e: Enemy): void {
    if (!e.alive) return;
    e.alive = false;
    this.free.push(e.index);
    if (e.def.behavior === 'static') this.braziers -= 1;
    else this.hostile -= 1;
    if (e === this.boss) this.boss = null;
    this.world.onEnemyKilled(e);
    if (e.elite && e.eliteAffix === 'splitter') {
      // Read before spawning: the first child reuses this enemy's slot (the free list is LIFO), so e is overwritten.
      const { x, z, maxHp } = e;
      for (let k = 0; k < 2; k += 1) {
        const c = this.spawn(e.def, x + (k ? 0.75 : -0.75), z, false);
        if (c) {
          c.maxHp = c.hp = maxHp * 0.3;
          c.speed *= 1.25;
          c.scale = e.def.scale * 1.15;
          c.radius = e.def.radius * 1.15;
          c.kx = k ? 5 : -5;
          c.born = this.world.time - 1;
        }
      }
    }
    if (e.def.behavior === 'splitter' && e.def.child) {
      const child = enemyById(e.def.child);
      for (let k = 0; k < 2; k += 1) {
        const c = this.spawn(child, e.x + (k ? 0.5 : -0.5), e.z);
        if (c) {
          c.kx = (k ? 4 : -4);
          c.born = this.world.time - 1;
        }
      }
    }
  }

  // Returns true if this hit killed the enemy.
  applyDamage(e: Enemy, amount: number, dirX: number, dirZ: number, force: number): boolean {
    if (!e.alive) return false;
    const w = this.world;
    const dist = e.eliteAffix === 'bulwark' ? Math.hypot(e.x - w.player.x, e.z - w.player.z) : 0;
    const effAmount = amount * eliteDamageMultiplier(e.eliteAffix, dist) * (w.time < e.vulnUntil ? 1.25 : 1);
    e.hp -= effAmount;
    e.flash = 0.12;
    if (force > 0 && e.mass < 100) {
      e.kx += (dirX * force) / e.mass;
      e.kz += (dirZ * force) / e.mass;
    }
    if (e.hp <= 0) {
      this.kill(e);
      return true;
    }
    return false;
  }

  slow(e: Enemy, amount: number, duration: number): void {
    const now = this.world.time;
    if (e.boss) amount = Math.min(amount, 0.35);
    if (amount >= e.slowAmt || now > e.slowUntil) e.slowAmt = amount;
    e.slowUntil = Math.max(e.slowUntil, now + duration);
  }

  // --- queries (allocation-free; results go into caller-provided arrays) ---

  queryCircle(x: number, z: number, r: number, out: Enemy[]): number {
    const n = this.grid.query(x, z, r + 2.5, this.scratch);
    let count = 0;
    for (let i = 0; i < n; i += 1) {
      const e = this.pool[this.scratch[i]];
      if (!e.alive) continue;
      const rr = r + e.radius;
      const dx = e.x - x;
      const dz = e.z - z;
      if (dx * dx + dz * dz <= rr * rr) out[count++] = e;
    }
    return count;
  }

  nearest(x: number, z: number, maxDist: number, exclude?: (e: Enemy) => boolean): Enemy | null {
    let best: Enemy | null = null;
    let bestD = maxDist * maxDist;
    for (const e of this.active) {
      if (!e.alive || e.def.behavior === 'static' || (exclude && exclude(e))) continue;
      const d = (e.x - x) ** 2 + (e.z - z) ** 2;
      if (d < bestD) {
        bestD = d;
        best = e;
      }
    }
    return best;
  }

  randomNear(x: number, z: number, maxDist: number): Enemy | null {
    const r2 = maxDist * maxDist;
    let chosen: Enemy | null = null;
    let seen = 0;
    for (const e of this.active) {
      if (!e.alive || e.def.behavior === 'static') continue;
      if ((e.x - x) ** 2 + (e.z - z) ** 2 > r2) continue;
      seen += 1;
      if (this.world.rng() * seen < 1) chosen = e; // reservoir sample
    }
    return chosen;
  }

  // --- simulation ---

  update(dt: number): void {
    const w = this.world;
    const now = w.time;
    const px = w.player.x;
    const pz = w.player.z;
    this.director(dt);
    const warlords = this.warlords;
    warlords.length = 0;
    for (const e of this.active) {
      if (e.alive && e.elite && e.eliteAffix === 'warlord') warlords.push(e);
    }

    let keep = 0;
    for (let i = 0; i < this.active.length; i += 1) {
      const e = this.active[i];
      if (!e.alive) {
        e.listed = false;
        continue;
      }
      this.active[keep++] = e;
      e.flash = Math.max(0, e.flash - dt);
      const dx = px - e.x;
      const dz = pz - e.z;
      const dist = Math.hypot(dx, dz) || 0.001;
      const nx = dx / dist;
      const nz = dz / dist;
      const frozen = now < e.slowUntil && e.slowAmt >= 1;
      let warlordMult = 1;
      if (!e.boss && !e.elite && warlords.length > 0) {
        for (const wl of warlords) {
          warlordMult = Math.max(warlordMult, warlordSpeedMultiplier(Math.hypot(e.x - wl.x, e.z - wl.z)));
        }
      }
      if (e.elite && e.eliteAffix && Math.floor(now * 0.8) !== Math.floor((now - dt) * 0.8)) {
        w.shapes.ring(e.x, e.z, e.radius * 0.9, e.radius * 1.6, rgb(ELITE_AFFIX_COLORS[e.eliteAffix]), 0.45, 1.4);
      }
      const speed = frozen ? 0 : e.speed * warlordMult * (now < e.slowUntil ? 1 - e.slowAmt : 1);
      let mx = 0;
      let mz = 0;

      if (e.boss) {
        // Pulsing ground ring so the boss always reads against dark scenery.
        if (Math.floor(now * 1.6) !== Math.floor((now - dt) * 1.6)) {
          w.shapes.ring(e.x, e.z, e.radius * 1.1, e.radius * 1.8, rgb(e.enraged ? '#ff3030' : e.def.glow), 0.6, 1.4);
        }
        const m = this.bossBrain(e, dt, now, nx, nz, dist, speed);
        mx = m.x;
        mz = m.z;
      } else if (e.lockUntil > now) {
        mx = e.dirX * speed * 1.3;
        mz = e.dirZ * speed * 1.3;
      } else if (!frozen) {
        switch (e.def.behavior) {
          case 'weave': {
            const s = Math.sin(now * 3 + e.phase) * 0.7;
            mx = (nx - nz * s) * speed;
            mz = (nz + nx * s) * speed;
            break;
          }
          case 'ranged':
          case 'summoner': {
            const keepAt = e.def.behavior === 'summoner' ? 8 : 7;
            const k = dist > keepAt + 1 ? 1 : dist < keepAt - 1.5 ? -0.8 : 0;
            const strafe = Math.sin(e.phase + now * 0.7) > 0 ? 0.6 : -0.6;
            mx = (nx * k - nz * strafe * (k === 0 ? 1 : 0.3)) * speed;
            mz = (nz * k + nx * strafe * (k === 0 ? 1 : 0.3)) * speed;
            e.cd -= dt;
            if (e.state === 0 && e.cd <= 0 && dist < 14) {
              e.state = 1;
              e.timer = 0.5;
            } else if (e.state === 1) {
              mx *= 0.2;
              mz *= 0.2;
              e.timer -= dt;
              e.flash = Math.max(e.flash, 0.03);
              if (e.timer <= 0) {
                e.state = 0;
                if (e.def.behavior === 'summoner' && e.def.child) {
                  e.cd = 6 + w.rng() * 2;
                  const child = enemyById(e.def.child);
                  for (let k2 = 0; k2 < 3; k2 += 1) {
                    const a = (k2 / 3) * Math.PI * 2;
                    this.spawn(child, e.x + Math.cos(a) * 1.4, e.z + Math.sin(a) * 1.4);
                  }
                  w.shapes.ring(e.x, e.z, 0.4, 2.2, rgb(e.def.glow), 0.5, 2.5);
                } else {
                  e.cd = 2.4 + w.rng() * 1.4;
                  this.fireBullet(e.x, e.z, nx, nz, 6.5, e.damage);
                }
              }
            }
            break;
          }
          case 'charger': {
            e.cd -= dt;
            if (e.state === 0) {
              mx = nx * speed;
              mz = nz * speed;
              if (dist < 8 && e.cd <= 0) {
                e.state = 1;
                e.timer = 0.7;
                e.dirX = nx;
                e.dirZ = nz;
                w.shapes.beam(e.x, e.z, e.x + nx * 10, e.z + nz * 10, 1.1, rgb('#ff3030'), 0.7, 1.3, 0.08, 1);
              }
            } else if (e.state === 1) {
              e.timer -= dt;
              if (e.timer <= 0) {
                e.state = 2;
                e.timer = 0.5;
              }
            } else {
              e.timer -= dt;
              mx = e.dirX * Math.max(speed, 1) * 4.2;
              mz = e.dirZ * Math.max(speed, 1) * 4.2;
              if (e.timer <= 0) {
                e.state = 0;
                e.cd = 2.8 + w.rng() * 2;
              }
            }
            break;
          }
          case 'exploder': {
            if (e.state === 0) {
              mx = nx * speed;
              mz = nz * speed;
              if (dist < 1.9) {
                e.state = 1;
                e.timer = 0.7;
                w.shapes.telegraph(e.x, e.z, 2.3, rgb('#ff5a1f'), 0.7);
              }
            } else {
              mx = nx * speed * 0.25;
              mz = nz * speed * 0.25;
              e.timer -= dt;
              if (e.timer <= 0) {
                if (dist < 2.3 + w.player.radius) w.hurtPlayer(e.damage, e.def.id);
                const c = this.fxColor.copy(rgb(e.def.glow));
                w.particles.burst(e.x, 0.6, e.z, 26, c.multiplyScalar(3), 7, 0.5, 0.5);
                w.shapes.ring(e.x, e.z, 0.3, 2.4, c, 0.35, 1.5);
                w.audio.explode(0.6);
                e.xp = 0;
                this.kill(e);
                continue;
              }
            }
            break;
          }
          case 'static':
            break;
          default:
            mx = nx * speed;
            mz = nz * speed;
        }
      }

      e.x += (mx + e.kx) * dt;
      e.z += (mz + e.kz) * dt;
      const damp = Math.exp(-7 * dt);
      e.kx *= damp;
      e.kz *= damp;
      if (mx * mx + mz * mz > 0.01) {
        let diff = Math.atan2(mx, mz) - e.facing;
        diff = Math.atan2(Math.sin(diff), Math.cos(diff));
        e.facing += diff * Math.min(1, dt * 10);
      }

      // Left far behind: re-enter ahead of the player instead of trailing forever.
      if (!e.boss && e.def.behavior !== 'static' && dist > this.spawnRadius * 1.7) {
        const p = this.ringPoint(this.spawnRadius, true);
        e.x = p.x;
        e.z = p.z;
        e.lockUntil = 0;
      } else if (e.def.behavior === 'static' && dist > this.spawnRadius * 2) {
        e.xp = 0;
        e.alive = false;
        this.free.push(e.index);
        this.braziers -= 1;
      }
    }
    this.active.length = keep;

    for (let i = 0; i < keep; i += 1) this.ids[i] = this.active[i].index;
    this.grid.rebuild(keep, this.xs, this.zs, this.ids, px, pz);
    this.resolveCollisions(px, pz);
    this.updateBullets(dt);
    this.render(now);
  }

  private resolveCollisions(px: number, pz: number): void {
    const w = this.world;
    const pr = w.player.radius;
    for (const e of this.active) {
      if (!e.alive) continue;
      const n = this.grid.query(e.x, e.z, e.radius + 1.3, this.scratch);
      for (let i = 0; i < n; i += 1) {
        const o = this.pool[this.scratch[i]];
        if (o.index <= e.index || !o.alive) continue;
        const dx = o.x - e.x;
        const dz = o.z - e.z;
        const rr = e.radius + o.radius;
        const d2 = dx * dx + dz * dz;
        if (d2 >= rr * rr) continue;
        const d = Math.sqrt(d2) || 0.01;
        const overlap = (rr - d) * 0.5;
        const total = e.mass + o.mass;
        const ux = dx / d;
        const uz = dz / d;
        e.x -= ux * overlap * (o.mass / total) * 1.6;
        e.z -= uz * overlap * (o.mass / total) * 1.6;
        o.x += ux * overlap * (e.mass / total) * 1.6;
        o.z += uz * overlap * (e.mass / total) * 1.6;
      }
      for (const s of this.solids) {
        const dx = e.x - s.x;
        const dz = e.z - s.z;
        const rr = s.r + e.radius * 0.8;
        const d2 = dx * dx + dz * dz;
        if (d2 < rr * rr && !e.boss) {
          const d = Math.sqrt(d2) || 0.01;
          e.x = s.x + (dx / d) * rr;
          e.z = s.z + (dz / d) * rr;
        }
      }
      if (e.def.behavior === 'static') {
        if ((e.x - px) ** 2 + (e.z - pz) ** 2 < (e.radius + pr * 0.9) ** 2) this.kill(e);
        continue;
      }
      if (e.def.damage <= 0) continue;
      const dx = e.x - px;
      const dz = e.z - pz;
      const rr = e.radius + pr * 0.9;
      if (dx * dx + dz * dz < rr * rr) w.hurtPlayer(e.damage * (e.boss && e.state === 2 ? 1.5 : 1), e.def.id);
    }
  }

  // --- spawning ---

  private director(dt: number): void {
    const w = this.world;
    const t = w.time;
    const stage = w.stage;
    if (t < stage.duration || this.boss || w.endless) {
      this.spawnAcc += spawnRate(t, stage, w.stormTier ?? 0) * (w.spawnRateMult ?? 1) * dt * (this.boss ? 0.35 : 1);
      const cap = maxAlive(t);
      const weights = this.spawnAcc >= 1 ? rosterWeights(t, stage) : NO_WEIGHTS; // built only on frames that spawn
      let total = 0;
      for (const [, wt] of weights) total += wt;
      while (this.spawnAcc >= 1) {
        this.spawnAcc -= 1;
        if (this.hostile >= cap) continue;
        let roll = w.rng() * total;
        let id = weights[0][0];
        for (const [wid, wt] of weights) {
          roll -= wt;
          if (roll <= 0) {
            id = wid;
            break;
          }
        }
        const p = this.ringPoint(this.spawnRadius, false);
        this.spawn(enemyById(id), p.x, p.z);
      }
    }

    const once = (key: string, when: number, fn: () => void) => {
      if (t >= when && !this.eventsDone.has(key)) {
        this.eventsDone.add(key);
        fn();
      }
    };
    for (const et of RING_EVENTS) {
      once(`ring${et}`, et, () => {
        const n = 16 + w.stageIndex * 3;
        const def = enemyById(stage.swarmer);
        for (let k = 0; k < n; k += 1) {
          const a = (k / n) * Math.PI * 2;
          this.spawn(def, w.player.x + Math.cos(a) * 13, w.player.z + Math.sin(a) * 13);
        }
        w.banner(tr('banner.surrounded'), tr('banner.surroundedSub'), '#ff7a7a');
      });
    }
    for (const et of FLOCK_EVENTS) {
      once(`flock${et}`, et, () => {
        const def = enemyById(stage.flyer);
        const a = w.rng() * Math.PI * 2;
        const cx = w.player.x + Math.cos(a) * 22;
        const cz = w.player.z + Math.sin(a) * 22;
        for (let k = 0; k < 22; k += 1) {
          const e = this.spawn(def, cx + (w.rng() - 0.5) * 6, cz + (w.rng() - 0.5) * 6);
          if (!e) break;
          e.dirX = -Math.cos(a);
          e.dirZ = -Math.sin(a);
          e.lockUntil = t + 10;
        }
        w.banner(tr('banner.swarm'), tr('banner.swarmSub'), '#ffcf6b');
      });
    }
    ELITE_TIMES.forEach((et, i) => {
      once(`elite${i}`, et, () => {
        const p = this.ringPoint(this.spawnRadius * 0.85, true);
        const spawned = this.spawn(enemyById(stage.elite), p.x, p.z, true);
        const affixLabel = spawned?.eliteAffix ? `${tr(`affix.${spawned.eliteAffix}` as UiKey)} · ` : '';
        w.banner(tr('banner.elite'), `${affixLabel}${tr('banner.eliteSub')}`, spawned?.eliteAffix ? ELITE_AFFIX_COLORS[spawned.eliteAffix] : '#ffd24d');
        w.audio.roar(0.5);
      });
    });
    once('boss', stage.duration, () => {
      const def = enemyById(stage.boss);
      const a = Math.atan2(w.player.vz, w.player.vx) || -Math.PI / 2;
      this.spawn(def, w.player.x + Math.cos(a) * 14, w.player.z + Math.sin(a) * 14);
      const text = enemyText(def.id);
      w.banner(text.name, text.title ?? '', stage.visuals.accent);
      w.audio.roar(1);
      w.shake(0.6);
    });
    if (w.endless && t >= stage.duration + 120) {
      const extra = Math.floor((t - stage.duration) / 120);
      once(`boss_ot_${extra}`, stage.duration + extra * 120, () => {
        if (this.boss) return; // only two bosses are drawn: one still alive and this one, at most
        const def = enemyById(stage.boss);
        const a = Math.atan2(w.player.vz, w.player.vx) || -Math.PI / 2;
        this.spawn(def, w.player.x + Math.cos(a) * 14, w.player.z + Math.sin(a) * 14);
        const text = enemyText(def.id);
        w.banner(text.name, text.title ?? '', stage.visuals.accent);
        w.audio.roar(1);
        w.shake(0.6);
      });
    }
    if (t >= this.nextBrazier) {
      this.nextBrazier = t + 16 + w.rng() * 6;
      if (this.braziers < 3) {
        const a = w.rng() * Math.PI * 2;
        const r = 9 + w.rng() * 6;
        this.spawn(enemyById('brazier'), w.player.x + Math.cos(a) * r, w.player.z + Math.sin(a) * r);
      }
    }
  }

  private ringPoint(radius: number, ahead: boolean): { x: number; z: number } {
    const w = this.world;
    let a = w.rng() * Math.PI * 2;
    const moving = Math.hypot(w.player.vx, w.player.vz) > 0.5;
    if (ahead && moving) a = Math.atan2(w.player.vz, w.player.vx) + (w.rng() - 0.5) * Math.PI;
    return { x: w.player.x + Math.cos(a) * radius, z: w.player.z + Math.sin(a) * radius };
  }

  // --- boss ---

  private bossBrain(e: Enemy, dt: number, _now: number, nx: number, nz: number, dist: number, speed: number): { x: number; z: number } {
    const w = this.world;
    const glow = rgb(e.def.glow);
    if (!e.enraged && e.hp < e.maxHp * 0.5) {
      e.enraged = true;
      e.speed *= 1.25;
      w.banner(tr('banner.enraged'), enemyText(e.def.id).name, '#ff4d4d');
      w.shapes.ring(e.x, e.z, 1, 11, rgb('#ff4d4d'), 0.65, 3);
      w.shapes.disc(e.x, e.z, 1, 7, glow, 0.45, 1.4);
      if (dist < 9) {
        w.player.group.position.x += nx * 3.8;
        w.player.group.position.z += nz * 3.8;
      }
      w.audio.roar(1);
      w.shake(0.5);
    }
    const attacks: BossAttack[] = (e.enraged ? e.def.enraged : e.def.attacks) ?? ['slam'];
    e.cd -= dt;
    if (e.state === 0) {
      if (e.cd <= 0) {
        const attack = attacks[e.seq++ % attacks.length];
        this.startBossAttack(e, attack, nx, nz, glow);
      }
      return dist > 2.5 ? { x: nx * speed, z: nz * speed } : { x: 0, z: 0 };
    }
    e.timer -= dt;
    if (e.state === 1) {
      // Wind-up for ring/charge: stand still and glow.
      e.flash = Math.max(e.flash, 0.04);
      if (e.timer <= 0) {
        if (e.dirX === 0 && e.dirZ === 0) {
          const n = e.enraged ? 26 : 18;
          for (let k = 0; k < n; k += 1) {
            const a = (k / n) * Math.PI * 2 + e.phase;
            this.fireBullet(e.x, e.z, Math.cos(a), Math.sin(a), 5.5, e.damage * 0.6);
          }
          e.phase += 0.3;
          w.shapes.ring(e.x, e.z, 0.5, 4, glow, 0.4, 2.5);
          w.audio.cast('nova');
          this.endBossAttack(e);
        } else {
          e.state = 2;
          e.timer = 0.8;
          w.audio.dash();
        }
      }
      return { x: 0, z: 0 };
    }
    if (e.state === 2) {
      if (e.timer <= 0) this.endBossAttack(e);
      w.particles.emit(e.x, 0.5, e.z, 0, 1, 0, glow, 1.2, 0.5);
      return { x: e.dirX * 16, z: e.dirZ * 16 };
    }
    // state 3: spiral (lockUntil doubles as the fire timer; bosses never flock)
    e.lockUntil -= dt;
    if (e.lockUntil <= 0) {
      e.lockUntil = 0.11;
      const arms = e.enraged ? 4 : 3;
      for (let k = 0; k < arms; k += 1) {
        const a = e.phase + (k / arms) * Math.PI * 2;
        this.fireBullet(e.x, e.z, Math.cos(a), Math.sin(a), 5, e.damage * 0.5);
      }
      e.phase += 0.32;
    }
    if (e.timer <= 0) {
      e.lockUntil = 0;
      this.endBossAttack(e);
    }
    return { x: nx * speed * 0.3, z: nz * speed * 0.3 };
  }

  private startBossAttack(e: Enemy, attack: BossAttack, nx: number, nz: number, glow: THREE.Color): void {
    const w = this.world;
    const red = rgb('#ff3030');
    e.dirX = 0;
    e.dirZ = 0;
    switch (attack) {
      case 'slam': {
        const n = e.enraged ? 3 : 1;
        for (let k = 0; k < n; k += 1) {
          const ox = k === 0 ? 0 : (w.rng() - 0.5) * 7;
          const oz = k === 0 ? 0 : (w.rng() - 0.5) * 7;
          w.hazards.dangerZone(w.player.x + ox, w.player.z + oz, 3.2, 1.1, e.damage, red, true);
        }
        this.endBossAttack(e);
        return;
      }
      case 'ring':
        e.state = 1;
        e.timer = 0.6;
        w.shapes.disc(e.x, e.z, e.radius, e.radius * 2.2, glow, 0.6, 1.2, 1);
        return;
      case 'charge':
        e.state = 1;
        e.timer = 0.9;
        e.dirX = nx;
        e.dirZ = nz;
        w.shapes.beam(e.x, e.z, e.x + nx * 14, e.z + nz * 14, e.radius * 2, red, 0.9, 1.2, 0.08, 1);
        return;
      case 'summon': {
        const def = enemyById(w.stage.swarmer);
        const n = e.enraged ? 12 : 8;
        for (let k = 0; k < n; k += 1) {
          const a = (k / n) * Math.PI * 2;
          const x = w.player.x + Math.cos(a) * 9;
          const z = w.player.z + Math.sin(a) * 9;
          this.spawn(def, x, z);
          w.shapes.ring(x, z, 0.2, 1.2, glow, 0.5, 2);
        }
        this.endBossAttack(e);
        return;
      }
      case 'spiral':
        e.state = 3;
        e.timer = 2.6;
        e.lockUntil = 0;
        return;
      case 'rain': {
        const n = e.enraged ? 9 : 6;
        for (let k = 0; k < n; k += 1) {
          const ox = k === 0 ? 0 : (w.rng() - 0.5) * 12;
          const oz = k === 0 ? 0 : (w.rng() - 0.5) * 12;
          w.hazards.dangerZone(w.player.x + ox, w.player.z + oz, 1.9, 1.25 + k * 0.06, e.damage * 0.7, glow, true);
        }
        this.endBossAttack(e);
      }
    }
  }

  private endBossAttack(e: Enemy): void {
    e.state = 0;
    e.cd = e.enraged ? 1.7 : 2.5;
    e.dirX = 0;
    e.dirZ = 0;
  }

  // --- bullets ---

  fireBullet(x: number, z: number, dx: number, dz: number, speed: number, damage: number): void {
    const b = this.bullets.find((bb) => !bb.active);
    if (!b) return;
    b.active = true;
    b.x = x + dx * 0.6;
    b.z = z + dz * 0.6;
    b.vx = dx * speed;
    b.vz = dz * speed;
    b.life = 5;
    b.damage = damage;
    b.radius = 0.32;
  }

  private updateBullets(dt: number): void {
    const w = this.world;
    const px = w.player.x;
    const pz = w.player.z;
    const time = w.time;
    let n = 0;
    for (const b of this.bullets) {
      if (!b.active) continue;
      b.life -= dt;
      b.x += b.vx * dt;
      b.z += b.vz * dt;
      const dx = b.x - px;
      const dz = b.z - pz;
      const rr = b.radius + w.player.radius * 0.8;
      if (dx * dx + dz * dz < rr * rr && !w.player.dashing) {
        w.hurtPlayer(b.damage, 'killer.projectile');
        b.active = false;
      }
      if (b.life <= 0) b.active = false;
      if (!b.active) continue;
      const pulse = 1 + Math.sin(time * 20 + b.x) * 0.12;
      this.posV.set(b.x, 0.7, b.z);
      this.scaleV.setScalar(b.radius * 2 * pulse);
      this.matrix.compose(this.posV, this.quat.identity(), this.scaleV);
      this.bulletMesh.setMatrixAt(n, this.matrix);
      this.color.copy(BULLET_COLOR).multiplyScalar(3.2);
      this.bulletMesh.setColorAt(n, this.color);
      n += 1;
    }
    this.bulletMesh.count = n;
    this.bulletMesh.visible = n > 0;
    uploadFirst(this.bulletMesh.instanceMatrix, n);
    uploadFirst(this.bulletMesh.instanceColor, n);
  }

  get bulletCount(): number {
    return this.bulletMesh.count;
  }

  // --- rendering ---

  private ensureGroup(def: EnemyDef): RenderGroup {
    const existing = this.groups.get(def.id);
    if (existing) return existing;
    const model = creatureModel(def.model, def.tint);
    const cap = def.behavior === 'boss' ? 2 : def.behavior === 'static' ? 8 : CAPACITY;
    // Dark maps' dim lights sink enemies into the ground; a self-glow in their own colour keeps them readable.
    const darkGround = new THREE.Color(this.world.stage.visuals.ground[1]).getHSL({ h: 0, s: 0, l: 0 }, THREE.SRGBColorSpace).l < 0.25;
    const bodyMat = litMaterial({
      vertexColors: true,
      flatShading: true,
      emissive: darkGround ? new THREE.Color(def.tint).multiplyScalar(DARK_MAP_GLOW) : 0x000000,
      roughness: 0.72,
      metalness: 0.05,
      transparent: def.model === 'wraith',
      opacity: def.model === 'wraith' ? 0.82 : 1,
    });
    const body = new THREE.InstancedMesh(model.body, bodyMat, cap);
    body.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3).fill(1), 3);
    body.castShadow = true;
    // Own shadow material: three's shared one would switch shaders each frame between casters with and without
    // per-instance colour (props), rebuilding its shader lookup every time.
    body.customDepthMaterial = new THREE.MeshDepthMaterial();
    body.frustumCulled = false;
    body.count = 0;
    body.visible = false;
    this.root.add(body);
    let glow: THREE.InstancedMesh | null = null;
    if (model.glow) {
      glow = new THREE.InstancedMesh(model.glow, new THREE.MeshBasicMaterial({ color: new THREE.Color(def.glow).multiplyScalar(2.2) }), cap);
      glow.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3).fill(1), 3);
      glow.frustumCulled = false;
      glow.count = 0;
      glow.visible = false;
      this.root.add(glow);
    }
    const group = { body, glow, n: 0 };
    this.groups.set(def.id, group);
    return group;
  }

  private render(now: number): void {
    for (const g of this.groups.values()) g.n = 0;
    const reduced = this.world.reducedFx;
    // Only enemies in view get drawn: swarms spawn and circle off-screen, and phone GPUs pay for every vertex.
    const cam = this.world.camera;
    this.frustum.setFromProjectionMatrix(this.viewProj.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse));
    for (const e of this.active) {
      if (!e.alive) continue;
      this.bounds.center.set(e.x, 1, e.z);
      this.bounds.radius = e.radius + CULL_MARGIN;
      if (!this.frustum.intersectsSphere(this.bounds)) continue;
      const g = e.group;
      if (g.n >= g.body.instanceMatrix.count) continue;
      const anim = reduced ? 0 : 1;
      const age = now - e.born;
      const pop = age < 0.25 ? easeOutBack(age / 0.25) : 1;
      let sx = 1;
      let sy = 1;
      let lift = 0;
      let jitter = 0;
      const wave = Math.sin(now * 10 + e.phase) * anim;
      switch (e.def.model) {
        case 'bat':
          sx = 1 + Math.sin(now * 22 + e.phase) * 0.28 * anim;
          lift = Math.sin(now * 5 + e.phase) * 0.15 * anim;
          break;
        case 'wraith':
        case 'eye':
          lift = 0.2 + Math.sin(now * 3 + e.phase) * 0.18 * anim;
          break;
        default:
          sy = 1 + wave * 0.07;
          sx = 1 - wave * 0.035;
      }
      if (e.def.behavior === 'exploder' && e.state === 1) sx = sy = 1 + Math.abs(Math.sin(now * 30)) * 0.25;
      if (e.def.behavior === 'charger' && e.state === 1) jitter = Math.sin(now * 80) * 0.08;
      if (e.boss) sy = 1 + Math.sin(now * 2.5) * 0.03;
      const s = e.scale * pop;
      this.posV.set(e.x + jitter, lift, e.z);
      this.quat.setFromAxisAngle(this.up, e.facing);
      this.scaleV.set(s * sx, s * sy, s * sx);
      this.matrix.compose(this.posV, this.quat, this.scaleV);
      g.body.setMatrixAt(g.n, this.matrix);
      if (e.flash > 0) this.color.setScalar(1 + (e.flash / 0.12) * 4);
      else if (now < e.slowUntil && e.slowAmt >= 1) this.color.setRGB(0.55, 1.1, 1.9);
      else if (now < e.slowUntil) this.color.setRGB(0.8, 0.95, 1.3);
      else if (e.boss && e.enraged) this.color.setRGB(2.2, 1.1, 0.95);
      else if (e.boss) this.color.setScalar(1.7);
      else if (e.elite) this.color.setRGB(1.3, 1.12, 0.75);
      else this.color.setScalar(1);
      g.body.setColorAt(g.n, this.color);
      if (g.glow) {
        g.glow.setMatrixAt(g.n, this.matrix);
        const gl = e.def.behavior === 'exploder' && e.state === 1 ? 2.2 : e.flash > 0 ? 1.8 : 1;
        this.color.setScalar(gl);
        g.glow.setColorAt(g.n, this.color);
      }
      g.n += 1;
    }
    for (const g of this.groups.values()) {
      g.body.count = g.n;
      g.body.visible = g.n > 0;
      uploadFirst(g.body.instanceMatrix, g.n);
      uploadFirst(g.body.instanceColor, g.n);
      if (g.glow) {
        g.glow.count = g.n;
        g.glow.visible = g.n > 0;
        uploadFirst(g.glow.instanceMatrix, g.n);
        uploadFirst(g.glow.instanceColor, g.n);
      }
    }
  }
}

function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}
